import { useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import Badge from 'components/partials/badge/badje';
import ActionsDropdown from 'components/partials/actions-dropdown';
import FilterItem from '../filter-item/filter-item';
import FavoriteModalForm from '../modals/favorite-modal-form';
import FavoriteModalRemove from '../modals/favorite-modal-remove';

import { formatDate } from 'helpers/dates-formatter';
import {
  getProductTypeDescription,
  type ProductTypeDictionary,
} from '../../reports-utils';
import type { FavoriteReportFilter } from '../filter-item/filter-item';

export interface FavoriteReportData {
  id: string | number;
  reportId: string | number;
  name: string;
  reportName: string;
  productType: string;
  bookmarkState?: string | null;
  metadata?: {
    filters?: FavoriteReportFilter[];
    [key: string]: unknown;
  } | null;
  lastModifiedDate?: string;
  [key: string]: unknown;
}

interface FavoriteReportTileProps {
  favoriteReport: FavoriteReportData;
  productTypesDictionary: ProductTypeDictionary;
  onFavoriteReportUpdated?: () => unknown;
  showActions?: boolean;
  onClick?: (event?: React.SyntheticEvent) => void;
  isSelected?: boolean;
}

const FavoriteReportTile = ({
  favoriteReport,
  productTypesDictionary,
  onFavoriteReportUpdated,
  showActions = true,
  onClick,
  isSelected = false,
}: FavoriteReportTileProps): React.JSX.Element => {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);

  const productTypeLabel = getProductTypeDescription(
    favoriteReport.productType,
    productTypesDictionary,
  );

  const filters = favoriteReport?.metadata?.filters;
  const isFiltersExist = !!filters?.length;

  const wrapperClassName = classNames(
    'w-full h-full flex flex-col text-left border rounded-md p-4 transition-all duration-200 hover:shadow-sm cursor-pointer',
    isSelected ? 'border-navy-800' : 'border-navy-200',
  );

  const actionsDropdown = showActions && (
    <ActionsDropdown
      options={[
        {
          label: 'Remove from favorites',
          onClick: () => setIsRemoveModalOpen(true),
        },
        {
          label: 'Rename',
          onClick: () => setIsEditModalOpen(true),
        },
      ]}
    />
  );

  const tileContent = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <Badge
          text={favoriteReport.reportName}
          variant="neutral"
          className="!rounded"
        />
        <Badge text={productTypeLabel} variant="neutral" className="!rounded" />
      </div>

      {isFiltersExist && (
        <div className="flex gap-2 flex-wrap">
          {filters!.map((filter, index) => (
            <FilterItem key={`${filter.column}-${index}`} filter={filter} />
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500 font-light mt-auto pt-4">
        Last edited:{' '}
        {formatDate(favoriteReport.lastModifiedDate, 'MMM D, YYYY h:mm A')}
      </div>
    </>
  );

  if (onClick) {
    return (
      <div
        className={wrapperClassName}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick(e)}
      >
        <div className="flex items-center justify-between">
          <div className="font-semibold text-gray-900 text-md">
            {favoriteReport.name}
          </div>
          {actionsDropdown}
        </div>
        {tileContent}
      </div>
    );
  }

  return (
    <>
      <div className={wrapperClassName}>
        <div className="flex items-center justify-between">
          <Link
            to={`/reports/${favoriteReport.reportId}?bookmarkId=${favoriteReport.id}`}
            className="font-semibold text-gray-900 text-md hover:underline"
          >
            {favoriteReport.name}
          </Link>
          {actionsDropdown}
        </div>
        <Link
          to={`/reports/${favoriteReport.reportId}?bookmarkId=${favoriteReport.id}`}
          className="flex flex-col flex-1"
        >
          {tileContent}
        </Link>
      </div>

      {isEditModalOpen && (
        <FavoriteModalForm
          reportId={favoriteReport.reportId}
          favoriteReport={favoriteReport}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={onFavoriteReportUpdated}
        />
      )}

      {isRemoveModalOpen && (
        <FavoriteModalRemove
          favoriteReport={favoriteReport}
          onClose={() => setIsRemoveModalOpen(false)}
          onSuccess={onFavoriteReportUpdated}
        />
      )}
    </>
  );
};

export default FavoriteReportTile;
